/**
 * Currency Converter - EUR to USD
 * Uses Frankfurter API (European Central Bank rates)
 * Caches rate in sessionStorage to minimize API calls
 */

class CurrencyConverter {
  constructor() {
    this.apiUrl = "https://api.frankfurter.app/latest?from=EUR&to=USD";
    this.cacheKey = "eurToUsdRate";
    this.cacheTimeKey = "eurToUsdRateTime";
    this.cacheDuration = 3600000; // 1 hour in milliseconds
    this.defaultRate = 1.1; // Fallback rate
    this.currentRate = null;
  }

  /**
   * Get cached rate if still valid
   */
  getCachedRate() {
    try {
      const cachedRate = sessionStorage.getItem(this.cacheKey);
      const cachedTime = sessionStorage.getItem(this.cacheTimeKey);

      if (cachedRate && cachedTime) {
        const age = Date.now() - parseInt(cachedTime);
        if (age < this.cacheDuration) {
          return parseFloat(cachedRate);
        }
      }
    } catch (e) {
      console.warn("Currency converter: sessionStorage not available", e);
    }
    return null;
  }

  /**
   * Cache the rate
   * @param {number} rate - The exchange rate to cache
   */
  cacheRate(rate) {
    try {
      sessionStorage.setItem(this.cacheKey, rate.toString());
      sessionStorage.setItem(this.cacheTimeKey, Date.now().toString());
    } catch (e) {
      console.warn("Currency converter: Cannot cache rate", e);
    }
  }

  /**
   * Fetch rate from API
   */
  async fetchRate() {
    try {
      const response = await fetch(this.apiUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      const rate = data.rates?.USD;

      if (rate && typeof rate === "number") {
        this.cacheRate(rate);
        return rate;
      }
      throw new Error("Invalid rate data");
    } catch (error) {
      console.warn(
        "Currency converter: API fetch failed, using default rate",
        error
      );
      return this.defaultRate;
    }
  }

  /**
   * Get current exchange rate (cached or fresh)
   */
  async getRate() {
    if (this.currentRate) {
      return this.currentRate;
    }

    // Try cache first
    const cachedRate = this.getCachedRate();
    if (cachedRate) {
      this.currentRate = cachedRate;
      return cachedRate;
    }

    // Fetch from API
    const rate = await this.fetchRate();
    this.currentRate = rate;
    return rate;
  }

  /**
   * Convert EUR cents to USD formatted string
   * @param {number} eurCents - Price in EUR cents
   * @returns {Promise<string>} Formatted USD price
   */
  async convertPrice(eurCents) {
    const rate = await this.getRate();
    const eurAmount = eurCents / 100;
    const usdAmount = eurAmount * rate;

    // Format as USD currency
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(usdAmount);
  }

  /**
   * Update all USD price elements on the page
   */
  async updatePrices() {
    const priceElements = document.querySelectorAll("[data-eur-price-cents]");

    if (priceElements.length === 0) {
      return;
    }

    const rate = await this.getRate();

    priceElements.forEach((element) => {
      if (!(element instanceof HTMLElement)) return;

      const eurCentsStr = element.dataset.eurPriceCents;
      if (!eurCentsStr) return;

      const eurCents = parseInt(eurCentsStr);
      if (isNaN(eurCents)) {
        return;
      }

      const eurAmount = eurCents / 100;
      const usdAmount = eurAmount * rate;

      const formattedUsd = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(usdAmount);

      element.textContent = `≈ ${formattedUsd}`;
      element.classList.remove("currency-loading");
    });
  }
}

// Initialize and update prices when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    const converter = new CurrencyConverter();
    converter.updatePrices();
  });
} else {
  const converter = new CurrencyConverter();
  converter.updatePrices();
}

// Export for use in other scripts
if (typeof window !== "undefined") {
  window.CurrencyConverter = CurrencyConverter;
}
